/**
 * AST Content marking utilities for the dual-version encryption system
 * Handles marking AST content for encryption without immediately encrypting it
 */

import CryptoJS from 'crypto-js'

import { hasSignificantContent } from './contentValidation'
import { ContentAST, EncryptedNode, InlineContent, TextNode } from '../transforms/ast'

export interface AstMarkingResult {
  markedAST: ContentAST
  sectionId: string
  contentHash: string
  success: boolean
  error?: string
}

export interface AstContentSection {
  sectionId: string
  content: InlineContent[]
  contentHash: string
}

export interface GroupedSection {
  sectionId: string
  contentHash: string
  /** Character tag this passage is about, when it is a character passage. */
  character?: string
  /** No key opens this passage: it is a character's, or the author holds it. */
  withheld?: boolean
  fragments: {
    content: InlineContent[]
  }[]
}

/**
 * Generate a content hash for tracking changes
 */
export function generateContentHash(content: InlineContent[]): string {
  const textContent = content
    .filter((node) => node.type === 'text')
    .map((node) => (node as TextNode).text)
    .join('')
  return CryptoJS.SHA256(textContent.trim()).toString()
}

/**
 * Extract all encrypted nodes from AST that represent marked sections
 */
export function extractMarkedSectionsFromAST(ast: ContentAST): AstContentSection[] {
  const sections: AstContentSection[] = []

  function traverseInlineContent(inlineContent: InlineContent[]) {
    inlineContent.forEach((node) => {
      if (node.type === 'encrypted') {
        sections.push({
          sectionId: node.id,
          content: node.content,
          contentHash: generateContentHash(node.content),
        })
      }
    })
  }

  function traverseBlocks(blocks: any[]) {
    blocks.forEach((block) => {
      switch (block.type) {
        case 'heading':
        case 'paragraph':
        case 'quote':
          traverseInlineContent(block.content)
          break
        case 'list':
          block.items.forEach((item: any) => {
            traverseInlineContent(item.content)
            if (item.children) {
              item.children.forEach((child: any) => traverseInlineContent(child.content))
            }
          })
          break
      }
    })
  }

  traverseBlocks(ast.blocks)
  return sections
}

/**
 * Group contiguous encrypted nodes that share the same sectionId
 * and compute a single content hash per group (stable across blocks).
 */
export function groupMarkedSectionsFromAST(ast: ContentAST): GroupedSection[] {
  const flat: { sectionId: string; content: InlineContent[]; character?: string; withheld?: boolean }[] = []

  const pushNode = (node: EncryptedNode) => {
    // Skip encrypted nodes with no significant content
    if (!hasSignificantContent(node.content)) {
      return
    }
    flat.push({
      sectionId: node.id,
      content: node.content,
      character: node.character,
      withheld: Boolean(node.character || node.authorHeld),
    })
  }

  const traverseInlineContent = (inlineContent: InlineContent[]) => {
    inlineContent.forEach((node) => {
      if (node.type === 'encrypted') {
        pushNode(node as EncryptedNode)
      }
    })
  }

  const traverseBlocks = (blocks: any[]) => {
    blocks.forEach((block) => {
      switch (block.type) {
        case 'heading':
        case 'paragraph':
        case 'quote':
          traverseInlineContent(block.content)
          break
        case 'list':
          block.items.forEach((item: any) => {
            traverseInlineContent(item.content)
            if (item.children) {
              item.children.forEach((child: any) => traverseInlineContent(child.content))
            }
          })
          break
      }
    })
  }

  traverseBlocks(ast.blocks)

  // Group by contiguous same sectionId
  const groups: GroupedSection[] = []
  let current: GroupedSection | null = null

  const computeHash = (content: InlineContent[]): string => {
    return generateContentHash(content)
  }

  for (const frag of flat) {
    if (current && current.sectionId === frag.sectionId) {
      current.fragments.push({ content: frag.content })
    } else {
      if (current) {
        // finalize previous hash
        const mergedContent = current.fragments.flatMap((f) => f.content)
        current.contentHash = computeHash(mergedContent)
      }
      current = {
        sectionId: frag.sectionId,
        contentHash: '',
        character: frag.character,
        withheld: frag.withheld,
        fragments: [{ content: frag.content }],
      }
      groups.push(current)
    }
  }

  if (current) {
    const mergedContent = current.fragments.flatMap((f) => f.content)
    current.contentHash = computeHash(mergedContent)
  }

  return groups
}

/**
 * Check if AST has any encrypted nodes (marked sections)
 */
export function hasMarkedSectionsInAST(ast: ContentAST): boolean {
  return extractMarkedSectionsFromAST(ast).length > 0
}

/**
 * Update an encrypted node in AST with new content while preserving section ID
 */
export function updateMarkedSectionInAST(
  ast: ContentAST,
  sectionId: string,
  newContent: InlineContent[],
): { ast: ContentAST; success: boolean; error?: string } {
  try {
    const updatedAST = JSON.parse(JSON.stringify(ast)) // Deep clone

    const updateInInlineContent = (inlineContent: InlineContent[]): boolean => {
      for (let i = 0; i < inlineContent.length; i++) {
        if (inlineContent[i].type === 'encrypted' && (inlineContent[i] as EncryptedNode).id === sectionId) {
          const encryptedNode = inlineContent[i] as EncryptedNode
          encryptedNode.content = newContent.filter((node) => node.type === 'text') as TextNode[]
          return true
        }
      }
      return false
    }

    const updateInBlocks = (blocks: any[]): boolean => {
      for (const block of blocks) {
        switch (block.type) {
          case 'heading':
          case 'paragraph':
          case 'quote':
            if (updateInInlineContent(block.content)) {
              return true
            }
            break
          case 'list':
            for (const item of block.items) {
              if (updateInInlineContent(item.content)) {
                return true
              }
              if (item.children) {
                for (const child of item.children) {
                  if (updateInInlineContent(child.content)) {
                    return true
                  }
                }
              }
            }
            break
        }
      }
      return false
    }

    const found = updateInBlocks(updatedAST.blocks)
    if (!found) {
      return {
        ast,
        success: false,
        error: `Section with ID ${sectionId} not found`,
      }
    }

    return {
      ast: updatedAST,
      success: true,
    }
  } catch (error) {
    return {
      ast,
      success: false,
      error: error instanceof Error ? error.message : 'Failed to update marked section',
    }
  }
}

/**
 * Validate that content hashes match for encrypted nodes in AST
 */
export function validateContentHashesInAST(ast: ContentAST): { valid: boolean; issues: string[] } {
  try {
    const sections = extractMarkedSectionsFromAST(ast)
    const issues: string[] = []

    sections.forEach((section) => {
      const calculatedHash = generateContentHash(section.content)

      // Note: In AST, we don't store separate content hashes, so we calculate them
      // This is mainly for validation against external hash storage
      if (section.contentHash !== calculatedHash) {
        issues.push(`Content hash mismatch for section ${section.sectionId}`)
      }
    })

    return {
      valid: issues.length === 0,
      issues,
    }
  } catch (error) {
    return {
      valid: false,
      issues: [error instanceof Error ? error.message : 'Validation failed'],
    }
  }
}
